/**
 * The four cards on top of the personal tab — what is missing up to yesterday,
 * today, what is logged and the month-end forecast — plus the one-line forecast
 * per project. The level is icon plus text, never colour alone.
 */
import styled from 'styled-components'
import type { ImputacionMonth } from '../../../types/imputacion'
import { findDay, fmtH, forecastByProject, levelInfo, previsionLine } from './imputacionLogic'

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
`

const Card = styled.div`
  background: linear-gradient(135deg, #E3F2FD, #fff);
  border: 1px solid #90CAF9;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
`

const CardLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.02em;
`

const CardValue = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  margin: 4px 0;
`

const CardSub = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`

const Level = styled.div<{ $tone: 'ok' | 'warn' | 'bad' | 'muted' }>`
  margin-top: 4px;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 700;
  color: ${({ $tone }) => ({ ok: '#2E7D32', warn: '#E65100', bad: '#C62828', muted: '#555' })[$tone]};
`

const Forecast = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`

export function ImputacionSummaryCards({ month }: { month: ImputacionMonth }) {
    const t = month.totals
    // An old month can no longer be logged from here: its gap is history, not a to-do.
    const closed = !month.month_editable
    const monthOver = month.today.slice(0, 7) > `${month.year}-${String(month.month).padStart(2, '0')}`
    const today = findDay(month, month.today)
    const level = levelInfo(t.level)
    const prevision = previsionLine(t, monthOver)
    const byProject = forecastByProject(month)

    let todayValue: string
    if (!today) todayValue = '—'
    else if (today.kind !== 'working' || today.expected_hours <= 0) todayValue = 'Hoy no es laborable'
    else if (t.pending_today <= 0.25) todayValue = 'Hoy completo ✓'
    else todayValue = `${fmtH(t.pending_today)} pendientes`

    return (
        <div>
            <Cards>
                <Card>
                    <CardLabel>{closed ? 'Faltaron' : 'Te faltan'}</CardLabel>
                    <CardValue>{fmtH(t.missing_to_date)}</CardValue>
                    <CardSub>
                        en {t.missing_days} {t.missing_days === 1 ? 'día' : 'días'}
                        {monthOver ? '' : ', hasta ayer'}
                    </CardSub>
                    <Level $tone={level.tone}>
                        <span aria-hidden="true">{level.icon}</span> {level.text}
                    </Level>
                </Card>
                <Card>
                    <CardLabel>Hoy</CardLabel>
                    <CardValue>{todayValue}</CardValue>
                    {!today && <CardSub>Hoy no está en este mes</CardSub>}
                </Card>
                <Card>
                    <CardLabel>Imputado este mes</CardLabel>
                    <CardValue>{fmtH(t.logged_month)}</CardValue>
                    <CardSub>de {fmtH(t.expected_month)}</CardSub>
                </Card>
                <Card>
                    <CardLabel>Previsión a fin de mes</CardLabel>
                    <CardValue>{prevision.main}</CardValue>
                    <CardSub>{prevision.sub}</CardSub>
                    {prevision.recover && <CardSub>{prevision.recover}</CardSub>}
                </Card>
            </Cards>
            {byProject && <Forecast>Previsión por proyecto: {byProject}</Forecast>}
        </div>
    )
}
